import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Eye, Printer, Share2, Download } from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const InvoiceViewer = ({ invoice, isOpen, onClose }) => {
  if (!invoice) return null;

  const logoUrl = 'https://customer-assets.emergentagent.com/job_edumanage-196/artifacts/5gc25pr5_GeniusKid%20Logo.svg';

  const generatePDF = () => {
    const doc = new jsPDF();
    
    // Add Logo
    const logoImg = new Image();
    logoImg.src = logoUrl;
    
    // Header with Logo
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('TAX INVOICE', 105, 25, { align: 'center' });
    
    // Company Info with Logo placeholder
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('GeniusKid Education', 14, 45);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('www.geniuskid.org', 14, 50);
    
    // Invoice Details
    doc.setFont('helvetica', 'bold');
    doc.text('Invoice Number:', 14, 65);
    doc.setFont('helvetica', 'normal');
    doc.text(`#${invoice.invoice_number}`, 60, 65);
    
    doc.setFont('helvetica', 'bold');
    doc.text('Invoice Date:', 14, 72);
    doc.setFont('helvetica', 'normal');
    doc.text(new Date(invoice.created_at).toLocaleDateString(), 60, 72);
    
    doc.setFont('helvetica', 'bold');
    doc.text('Due Date:', 14, 79);
    doc.setFont('helvetica', 'normal');
    doc.text(new Date(invoice.due_date).toLocaleDateString(), 60, 79);
    
    // Student Details
    doc.setFont('helvetica', 'bold');
    doc.text('Bill To:', 14, 95);
    doc.setFont('helvetica', 'normal');
    doc.text(invoice.student_name || 'Student', 14, 102);
    
    // Invoice Table
    const tableData = [
      [
        'Description',
        'Mode',
        'Frequency',
        'Amount'
      ],
      [
        'Monthly Fees',
        invoice.student_mode.toUpperCase(),
        invoice.fee_frequency.toUpperCase(),
        `₹${invoice.amount.toFixed(2)}`
      ]
    ];
    
    autoTable(doc, {
      startY: 115,
      head: [tableData[0]],
      body: [tableData[1]],
      theme: 'grid',
      headStyles: {
        fillColor: [59, 130, 246],
        textColor: 255,
        fontStyle: 'bold'
      },
      styles: {
        fontSize: 10,
        cellPadding: 5
      }
    });
    
    // Calculate totals
    const subtotal = invoice.amount;
    const royaltyAmount = invoice.royalty_amount || 0;
    const total = subtotal;
    
    const finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : 135;
    
    // Totals
    doc.setFont('helvetica', 'bold');
    doc.text('Subtotal:', 130, finalY);
    doc.setFont('helvetica', 'normal');
    doc.text(`₹${subtotal.toFixed(2)}`, 170, finalY, { align: 'right' });
    
    if (royaltyAmount > 0) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text(`(Includes Royalty: ₹${royaltyAmount.toFixed(2)})`, 130, finalY + 5);
      doc.setFontSize(10);
    }
    
    doc.setFont('helvetica', 'bold');
    doc.text('Total:', 130, finalY + 12);
    doc.text(`₹${total.toFixed(2)}`, 170, finalY + 12, { align: 'right' });
    
    // Status
    const statusY = finalY + 25;
    doc.setFont('helvetica', 'bold');
    doc.text('Status:', 14, statusY);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(
      invoice.status === 'paid' ? 34 : 
      invoice.status === 'overdue' ? 220 : 59,
      invoice.status === 'paid' ? 197 : 
      invoice.status === 'overdue' ? 38 : 130,
      invoice.status === 'paid' ? 94 : 
      invoice.status === 'overdue' ? 38 : 246
    );
    doc.text(invoice.status.toUpperCase(), 40, statusY);
    doc.setTextColor(0, 0, 0);
    
    // Notes
    if (invoice.notes) {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9);
      doc.text('Notes:', 14, statusY + 15);
      doc.setFont('helvetica', 'normal');
      const splitNotes = doc.splitTextToSize(invoice.notes, 180);
      doc.text(splitNotes, 14, statusY + 20);
    }
    
    // Footer
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.text('Thank you for your business!', 105, 280, { align: 'center' });
    
    return doc;
  };

  const handleViewPDF = () => {
    const doc = generatePDF();
    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, '_blank');
  };

  const handleDownloadPDF = () => {
    const doc = generatePDF();
    doc.save(`Invoice-${invoice.invoice_number}.pdf`);
    toast.success('Invoice downloaded successfully!');
  };

  const handlePrint = () => {
    const doc = generatePDF();
    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    const printWindow = window.open(pdfUrl, '_blank');
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  const handleShare = async () => {
    const doc = generatePDF();
    const pdfBlob = doc.output('blob');
    const file = new File([pdfBlob], `Invoice-${invoice.invoice_number}.pdf`, { type: 'application/pdf' });

    if (navigator.share && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          title: `Invoice #${invoice.invoice_number}`,
          text: `Invoice for ${invoice.student_name}`,
          files: [file]
        });
        toast.success('Invoice shared successfully!');
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error sharing:', error);
          fallbackShare();
        }
      }
    } else {
      fallbackShare();
    }
  };

  const fallbackShare = () => {
    const doc = generatePDF();
    doc.save(`Invoice-${invoice.invoice_number}.pdf`);
    toast.success('Invoice downloaded! You can now share it manually.');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Invoice #{invoice.invoice_number}</DialogTitle>
        </DialogHeader>

        {/* Invoice Preview */}
        <div className="border rounded-lg p-6 bg-gray-50">
          <div className="bg-white p-8 rounded shadow-sm">
            {/* Header */}
            <div className="border-b pb-6 mb-6">
              <div className="flex items-center justify-center gap-4 mb-4">
                <img src={logoUrl} alt="GeniusKid Logo" className="h-12" />
              </div>
              <h1 className="text-3xl font-bold text-center text-blue-600">TAX INVOICE</h1>
            </div>

            {/* Company and Invoice Info */}
            <div className="grid grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="font-bold text-lg mb-2">GeniusKid Education</h3>
                <p className="text-sm text-gray-600">www.geniuskid.org</p>
              </div>
              <div className="text-right">
                <div className="mb-2">
                  <span className="font-semibold">Invoice #:</span> {invoice.invoice_number}
                </div>
                <div className="mb-2">
                  <span className="font-semibold">Date:</span> {new Date(invoice.created_at).toLocaleDateString()}
                </div>
                <div>
                  <span className="font-semibold">Due Date:</span> {new Date(invoice.due_date).toLocaleDateString()}
                </div>
              </div>
            </div>

            {/* Bill To */}
            <div className="mb-8">
              <h3 className="font-bold mb-2">Bill To:</h3>
              <p className="text-lg">{invoice.student_name || 'Student'}</p>
            </div>

            {/* Invoice Items */}
            <table className="w-full mb-8">
              <thead className="bg-blue-500 text-white">
                <tr>
                  <th className="px-4 py-3 text-left">Description</th>
                  <th className="px-4 py-3 text-left">Mode</th>
                  <th className="px-4 py-3 text-left">Frequency</th>
                  <th className="px-4 py-3 text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="border">
                <tr className="border-t">
                  <td className="px-4 py-3">Monthly Fees</td>
                  <td className="px-4 py-3">{invoice.student_mode.toUpperCase()}</td>
                  <td className="px-4 py-3">{invoice.fee_frequency.toUpperCase()}</td>
                  <td className="px-4 py-3 text-right">₹{invoice.amount.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>

            {/* Totals */}
            <div className="flex justify-end mb-8">
              <div className="w-64">
                <div className="flex justify-between mb-2">
                  <span className="font-semibold">Subtotal:</span>
                  <span>₹{invoice.amount.toFixed(2)}</span>
                </div>
                {invoice.royalty_amount > 0 && (
                  <div className="flex justify-between mb-2 text-sm text-gray-600">
                    <span>(Includes Royalty:</span>
                    <span>₹{invoice.royalty_amount.toFixed(2)})</span>
                  </div>
                )}
                <div className="flex justify-between border-t pt-2 text-lg font-bold">
                  <span>Total:</span>
                  <span>₹{invoice.amount.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Status */}
            <div className="mb-6">
              <span className="font-semibold">Status: </span>
              <span className={`px-3 py-1 rounded-full text-sm ${
                invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
                invoice.status === 'overdue' ? 'bg-red-100 text-red-800' :
                'bg-yellow-100 text-yellow-800'
              }`}>
                {invoice.status.toUpperCase()}
              </span>
            </div>

            {/* Notes */}
            {invoice.notes && (
              <div className="mb-6">
                <h3 className="font-semibold mb-2">Notes:</h3>
                <p className="text-sm text-gray-600 italic">{invoice.notes}</p>
              </div>
            )}

            {/* Footer */}
            <div className="text-center text-sm text-gray-500 italic border-t pt-4">
              Thank you for your business!
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Button onClick={handleViewPDF} variant="outline" className="flex items-center gap-2">
            <Eye className="w-4 h-4" />
            View PDF
          </Button>
          <Button onClick={handleDownloadPDF} variant="outline" className="flex items-center gap-2">
            <Download className="w-4 h-4" />
            Download
          </Button>
          <Button onClick={handlePrint} variant="outline" className="flex items-center gap-2">
            <Printer className="w-4 h-4" />
            Print
          </Button>
          <Button onClick={handleShare} className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-purple-500">
            <Share2 className="w-4 h-4" />
            Share
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceViewer;
